import { useEffect, useRef, useState } from 'react';
import {
  ref as storageRef,
  listAll,
  getMetadata,
  getDownloadURL,
  uploadBytes,
  deleteObject,
} from 'firebase/storage';
import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc as fsDoc,
} from 'firebase/firestore';
import { storage, db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';
import { ClassifiedFiles } from './ClassifiedFiles.js';

/**
 * Windows-Explorer-style file manager over Cloud Storage.
 *  - folder navigation (Up / breadcrumb / quick roots)
 *  - sortable columns (name / type / size / modified)
 *  - multi-select (checkbox, shift-range, select-all) + keyboard Delete
 *  - bulk delete (recursive for folders) that also removes the linked
 *    Firestore document record + its extracted financial rows, so deleting a
 *    junk file actually purges it from Homebase rather than orphaning metadata.
 */

interface FolderEntry {
  kind: 'folder';
  name: string;
  fullPath: string;
}
interface FileEntry {
  kind: 'file';
  name: string;
  fullPath: string;
  size?: number;
  contentType?: string;
  updated?: string;
}
type Entry = FolderEntry | FileEntry;

const QUICK_ROOTS: Array<{ label: string; path: string }> = [
  { label: 'All', path: '' },
  { label: 'Uploads', path: 'uploads' },
  { label: 'Dropbox imports', path: 'uploads/dropbox' },
  { label: 'Manual uploads', path: 'uploads/unassigned' },
  { label: 'Extracted text', path: 'text' },
  { label: 'Logos', path: 'logos' },
];

type SortKey = 'name' | 'type' | 'size' | 'updated';

export function FileExplorer() {
  const { role } = useAuth();
  const isAdmin = role === 'admin';
  const [mode, setMode] = useState<'classified' | 'raw'>('classified');
  const [path, setPath] = useState<string>('uploads');
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [lastIdx, setLastIdx] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<1 | -1>(1);
  const [search, setSearch] = useState('');
  const [alsoPurgeRecords, setAlsoPurgeRecords] = useState(true);
  const [progress, setProgress] = useState<string | null>(null);
  const uploadInput = useRef<HTMLInputElement>(null);

  async function load(p: string) {
    setLoading(true);
    setErr(null);
    setSelected(new Set());
    setLastIdx(null);
    try {
      const listed = await listAll(storageRef(storage, p));
      const folders: FolderEntry[] = listed.prefixes.map((pref) => ({
        kind: 'folder',
        name: pref.name,
        fullPath: pref.fullPath,
      }));
      const files: FileEntry[] = await Promise.all(
        listed.items.map(async (item) => {
          try {
            const m = await getMetadata(item);
            return { kind: 'file' as const, name: item.name, fullPath: item.fullPath, size: m.size, contentType: m.contentType, updated: m.updated };
          } catch {
            return { kind: 'file' as const, name: item.name, fullPath: item.fullPath };
          }
        }),
      );
      setEntries([...folders, ...files]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'List failed');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  // Sorted + filtered view. Folders always first.
  const view = entries
    .filter((e) => !(e.kind === 'file' && e.name === '.keep')) // hide folder-marker placeholders
    .filter((e) => !search || e.name.toLowerCase().includes(search.toLowerCase()))
    .slice()
    .sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;
      let cmp = 0;
      if (sortKey === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortKey === 'size') cmp = ((a as FileEntry).size ?? 0) - ((b as FileEntry).size ?? 0);
      else if (sortKey === 'updated') cmp = ((a as FileEntry).updated ?? '').localeCompare((b as FileEntry).updated ?? '');
      else if (sortKey === 'type') cmp = extOf(a.name).localeCompare(extOf(b.name));
      return cmp * sortDir;
    });

  const crumbs = path.split('/').filter(Boolean);
  const parent = crumbs.slice(0, -1).join('/');

  function clickSort(k: SortKey) {
    if (k === sortKey) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortKey(k); setSortDir(1); }
  }

  function toggleRow(idx: number, e: React.MouseEvent | React.ChangeEvent) {
    const entry = view[idx];
    if (!entry) return;
    const next = new Set(selected);
    const isShift = 'shiftKey' in e && (e as React.MouseEvent).shiftKey && lastIdx !== null;
    if (isShift) {
      const [lo, hi] = [Math.min(lastIdx!, idx), Math.max(lastIdx!, idx)];
      for (let i = lo; i <= hi; i++) { const v = view[i]; if (v) next.add(v.fullPath); }
    } else {
      if (next.has(entry.fullPath)) next.delete(entry.fullPath);
      else next.add(entry.fullPath);
      setLastIdx(idx);
    }
    setSelected(next);
  }

  function toggleAll() {
    if (selected.size === view.length && view.length > 0) setSelected(new Set());
    else setSelected(new Set(view.map((e) => e.fullPath)));
  }

  async function onDownload(entry: FileEntry) {
    try {
      const url = await getDownloadURL(storageRef(storage, entry.fullPath));
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Download failed');
    }
  }

  // Recursively delete everything under a folder prefix.
  async function deletePrefix(prefix: string, onFile: (n: number) => void) {
    const listed = await listAll(storageRef(storage, prefix));
    for (const item of listed.items) {
      await deleteObject(item).catch(() => {});
      await purgeRecordFor(item.fullPath);
      onFile(1);
    }
    for (const pref of listed.prefixes) await deletePrefix(pref.fullPath, onFile);
  }

  // When deleting uploads/.../{docId}/{file}, also remove the Firestore doc +
  // its financial rows so the file is fully purged from Homebase.
  async function purgeRecordFor(objectPath: string) {
    if (!alsoPurgeRecords) return;
    if (!objectPath.startsWith('uploads/')) return;
    const parts = objectPath.split('/');
    const docId = parts[parts.length - 2];
    if (!docId) return;
    try {
      await deleteDoc(fsDoc(db, 'documents', docId)).catch(() => {});
      const fin = await getDocs(query(collection(db, 'financialRecords'), where('docId', '==', docId)));
      await Promise.all(fin.docs.map((d) => deleteDoc(d.ref).catch(() => {})));
    } catch {
      /* best-effort */
    }
  }

  async function deleteSelected() {
    if (!isAdmin || selected.size === 0) return;
    const items = view.filter((e) => selected.has(e.fullPath));
    const fileCount = items.filter((e) => e.kind === 'file').length;
    const folderCount = items.filter((e) => e.kind === 'folder').length;
    if (!confirm(
      `Delete ${selected.size} item(s)` +
        (folderCount ? ` (${folderCount} folder(s) — including everything inside)` : '') +
        `?\n\nThis permanently removes the file(s) from Cloud Storage` +
        (alsoPurgeRecords ? ' and their document records from Homebase' : '') +
        '. This cannot be undone.',
    )) return;

    let done = 0;
    let totalFiles = fileCount; // folders counted as we recurse
    const tick = (n: number) => { done += n; setProgress(`Deleting… ${done} file(s) removed`); };
    setProgress('Deleting…');
    try {
      for (const e of items) {
        if (e.kind === 'folder') {
          await deletePrefix(e.fullPath, tick);
        } else {
          await deleteObject(storageRef(storage, e.fullPath)).catch(() => {});
          await purgeRecordFor(e.fullPath);
          tick(1);
        }
      }
      setProgress(`Done — removed ${done} file(s).`);
      void totalFiles;
      await load(path);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setTimeout(() => setProgress(null), 2500);
    }
  }

  async function onNewFolder() {
    if (!isAdmin) return;
    const raw = prompt('New folder name:');
    const name = (raw ?? '').trim().replace(/[\\/]+/g, '').replace(/[^A-Za-z0-9 ._-]/g, '').trim();
    if (!name) return;
    // Cloud Storage has no real folders — a folder "exists" once an object lives
    // under its prefix. Drop a hidden .keep placeholder so the folder appears.
    const dest = `${path ? path.replace(/\/$/, '') + '/' : ''}${name}/.keep`;
    try {
      await uploadBytes(storageRef(storage, dest), new Blob([''], { type: 'text/plain' }), {
        contentType: 'text/plain',
        customMetadata: { placeholder: 'folder' },
      });
      await load(path);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not create folder');
    }
  }

  async function onUpload(files: FileList | File[]) {
    if (!isAdmin) return;
    setLoading(true);
    try {
      for (const f of Array.from(files)) {
        const cleaned = f.name.replace(/[^A-Za-z0-9._-]/g, '_').slice(0, 200);
        await uploadBytes(storageRef(storage, `${path.replace(/\/$/, '')}/${cleaned}`), f, {
          contentType: f.type || 'application/octet-stream',
        });
      }
      await load(path);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setLoading(false);
      if (uploadInput.current) uploadInput.current.value = '';
    }
  }

  const allChecked = view.length > 0 && selected.size === view.length;

  return (
    <div
      className="mx-auto w-full max-w-6xl space-y-3 px-6 py-6"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.key === 'Delete' || e.key === 'Backspace') && selected.size > 0 && isAdmin) {
          e.preventDefault();
          void deleteSelected();
        }
      }}
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-stone-400">App</div>
          <h1 className="text-2xl font-semibold text-stone-800">File explorer</h1>
          <p className="text-xs text-stone-500">
            {mode === 'classified'
              ? 'Files organized by the LLM classification — Category → Doc Type → documents. Click a file to open it; select and delete the ones that don’t belong.'
              : 'Raw Cloud Storage layout (folders are internal IDs). Use this to clean up junk objects; deletes also purge the matching document records.'}
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-stone-300 p-0.5 text-xs">
          <button
            type="button"
            onClick={() => setMode('classified')}
            className={`rounded px-3 py-1 font-medium ${mode === 'classified' ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-100'}`}
          >
            By classification
          </button>
          <button
            type="button"
            onClick={() => setMode('raw')}
            className={`rounded px-3 py-1 font-medium ${mode === 'raw' ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-100'}`}
          >
            Raw storage
          </button>
        </div>
      </header>

      {mode === 'classified' && <ClassifiedFiles />}
      {mode === 'classified' ? null : (
      <>
      <div className="hidden" />

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setPath(parent)} disabled={!path} className="rounded border border-stone-300 px-2 py-1 text-xs hover:bg-stone-50 disabled:opacity-40" type="button" title="Up one level">↑ Up</button>
        <button onClick={() => void load(path)} className="rounded border border-stone-300 px-2 py-1 text-xs hover:bg-stone-50" type="button" title="Refresh">⟳</button>
        <div className="flex flex-wrap gap-1">
          {QUICK_ROOTS.map((r) => (
            <button key={r.path || 'root'} onClick={() => setPath(r.path)} className={`rounded-full px-2.5 py-1 text-[11px] ${path === r.path ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`} type="button">
              {r.label}
            </button>
          ))}
        </div>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter this folder…" className="ml-auto h-7 w-44 rounded border border-stone-300 px-2 text-xs" />
        {isAdmin && (
          <>
            <button onClick={() => void onNewFolder()} className="rounded border border-stone-300 px-2 py-1 text-xs hover:bg-stone-50" type="button">📁 New folder</button>
            <button onClick={() => uploadInput.current?.click()} className="rounded border border-stone-300 px-2 py-1 text-xs hover:bg-stone-50" type="button">Upload here</button>
            <input ref={uploadInput} type="file" multiple className="hidden" onChange={(e) => e.target.files && void onUpload(e.target.files)} />
          </>
        )}
      </div>

      {/* Breadcrumbs */}
      <div className="flex flex-wrap items-center gap-1 text-xs">
        <button onClick={() => setPath('')} className="text-[color:var(--sfp-primary)] hover:underline" type="button">gs://…</button>
        {crumbs.map((c, i) => (
          <span key={i} className="flex items-center gap-1">
            <span className="text-stone-400">/</span>
            <button onClick={() => setPath(crumbs.slice(0, i + 1).join('/'))} className="text-[color:var(--sfp-primary)] hover:underline" type="button">{c}</button>
          </span>
        ))}
      </div>

      {/* Selection action bar */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#9C4A3A]/30 bg-[#9C4A3A]/5 px-3 py-2 text-xs">
          <span className="font-medium text-stone-700">{selected.size} selected</span>
          {isAdmin && (
            <button onClick={() => void deleteSelected()} className="rounded bg-[#9C4A3A] px-3 py-1 font-medium text-white hover:bg-[#7E3A2D]" type="button">
              Delete selected
            </button>
          )}
          <label className="flex items-center gap-1 text-stone-600">
            <input type="checkbox" checked={alsoPurgeRecords} onChange={(e) => setAlsoPurgeRecords(e.target.checked)} />
            also remove document records
          </label>
          <button onClick={() => setSelected(new Set())} className="text-stone-500 hover:text-stone-800" type="button">clear</button>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-stone-200 px-4 py-2 text-xs text-stone-500">
          <span>{loading ? 'Loading…' : `${view.length} item(s)`}</span>
          {progress && <span className="text-[#9C4A3A]">{progress}</span>}
        </div>
        {err && <div className="border-b border-[#9C4A3A] bg-[#9C4A3A]/10 px-4 py-2 text-xs text-[#9C4A3A]">{err}</div>}
        {view.length === 0 && !loading ? (
          <div className="px-4 py-10 text-center text-xs text-stone-400">Empty folder.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-left text-[10px] uppercase tracking-wider text-stone-500">
              <tr>
                <th className="w-8 px-3 py-2">
                  <input type="checkbox" checked={allChecked} onChange={toggleAll} title="Select all" />
                </th>
                <Th label="Name" k="name" sortKey={sortKey} sortDir={sortDir} onClick={clickSort} />
                <Th label="Type" k="type" sortKey={sortKey} sortDir={sortDir} onClick={clickSort} />
                <Th label="Size" k="size" sortKey={sortKey} sortDir={sortDir} onClick={clickSort} />
                <Th label="Modified" k="updated" sortKey={sortKey} sortDir={sortDir} onClick={clickSort} />
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {view.map((e, idx) => {
                const isSel = selected.has(e.fullPath);
                return (
                  <tr key={e.fullPath} className={`border-b border-stone-100 ${isSel ? 'bg-[color:var(--sfp-primary)]/5' : 'hover:bg-stone-50'}`}>
                    <td className="px-3 py-1.5">
                      <input type="checkbox" checked={isSel} onChange={(ev) => toggleRow(idx, ev)} onClick={(ev) => { if (ev.shiftKey) toggleRow(idx, ev); }} />
                    </td>
                    <td className="px-3 py-1.5">
                      {e.kind === 'folder' ? (
                        <button onClick={() => setPath(e.fullPath)} className="flex items-center gap-2 text-stone-800 hover:text-[color:var(--sfp-primary)]" type="button">
                          <span>📁</span><span className="font-medium">{e.name}</span>
                        </button>
                      ) : (
                        <button onClick={() => void onDownload(e)} className="flex items-center gap-2 text-left text-stone-800 hover:text-[color:var(--sfp-primary)]" type="button" title="Download / open">
                          <span>{kindGlyph((e as FileEntry).contentType, e.name)}</span><span className="truncate">{e.name}</span>
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-[11px] text-stone-500">{e.kind === 'folder' ? 'Folder' : (extOf(e.name).toUpperCase() || 'file')}</td>
                    <td className="px-3 py-1.5 text-xs text-stone-600">{e.kind === 'file' && (e as FileEntry).size != null ? bytesHuman((e as FileEntry).size!) : '—'}</td>
                    <td className="px-3 py-1.5 text-xs text-stone-500">{e.kind === 'file' && (e as FileEntry).updated ? new Date((e as FileEntry).updated!).toLocaleString() : '—'}</td>
                    <td className="px-3 py-1.5 text-right">
                      {isAdmin && (
                        <button
                          onClick={() => { setSelected(new Set([e.fullPath])); setTimeout(() => void deleteSelected(), 0); }}
                          className="text-xs text-[#9C4A3A] hover:underline"
                          type="button"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}
    </div>
  );
}

function Th({ label, k, sortKey, sortDir, onClick }: { label: string; k: SortKey; sortKey: SortKey; sortDir: 1 | -1; onClick: (k: SortKey) => void }) {
  return (
    <th className="px-3 py-2">
      <button onClick={() => onClick(k)} className="inline-flex items-center gap-1 hover:text-stone-800" type="button">
        {label}{sortKey === k && <span>{sortDir === 1 ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i > 0 ? name.slice(i + 1).toLowerCase() : '';
}

function bytesHuman(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function kindGlyph(ct: string | undefined, name: string): string {
  const ext = extOf(name);
  if (ct?.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'tif', 'tiff'].includes(ext)) return '🖼';
  if (ct === 'application/pdf' || ext === 'pdf') return '📕';
  if (ct?.includes('spreadsheet') || ['xlsx', 'xls', 'csv'].includes(ext)) return '📊';
  if (ct?.includes('word') || ['doc', 'docx'].includes(ext)) return '📝';
  if (ct?.startsWith('text/') || ['txt', 'md'].includes(ext)) return '📄';
  if (ct?.startsWith('video/')) return '🎬';
  if (ct?.startsWith('audio/')) return '🎵';
  return '▤';
}
